import { useIsHdEnabled } from "common/contexts/UIContext";

import Page from "client/common/components/layouts/Page";
import Subheader from "client/common/components/layouts/Subheader";
import Body from "client/common/components/layouts/Body";
import Markdown from "client/common/components/content/Markdown";
import StreamlinedHeader from "client/common/components/header/StreamlinedHeader";
import { useBreakpoints } from "client/common/hooks";
import { INITIAL_CLIENT_DATA } from "client/common/utils";
import { externalUrls as marketingExternalUrls } from "client/marketing/hooks/constants";

import styles from "../styles/PrivacyPolicyPage.module.less";

const LAST_UPDATED_DATE = "February 24, 2026";

/**
 *  Privacy policy is in markdown to make it easy to migrate down the road to a place that is maintainable without developer
 *  intervention.
 *  REMEMBER: If you change this, you must also update the last updated date.
 */
const getPrivacyPolicy = (privacyRightsUrl: string) => `
Welcome to your clinic's online storefront! We are excited to have you
here, and we hope that you are equally as excited to learn about how we
protect your personal information. Just like how we protect your furry
friends from fleas and ticks, we take the protection of your data
seriously.

We understand that your privacy is important and we want to make sure
that you feel safe and secure when sharing your personal information
with us. So, let's paws for a moment and introduce ourselves properly:
We are Vetcove, a software provider in the veterinary space. We have
teamed up with your veterinary clinic to power this online storefront
(the "**Platform**") on your clinic's behalf. We want to make sure that
you have an excellent experience using the Platform, and that includes
understanding how we handle and share any personal information shared
with us. In the interest of utmost trans-paw-rency, we encourage you to
read this Consumer Privacy Policy and Notice ("**Privacy Policy**") in
full to understand the information we collect, how we use it, and the
choices available to you.

This Privacy Policy applies to the information we collect from and about
you through your use of the Platform and related interactions with us
(collectively referred to herein as the "**Services**"). If you do not
agree to this Privacy Policy, please do not use the Services.

# 1. Scope

## 1.1 Jurisdiction and Applicable Laws

Vetcove does not have any locations outside of the United States
("**U.S**"), nor do we offer this Platform and related services,
including purchase of any products through the Platform, outside of the
U.S. As our products and services are intended for U.S. businesses and
U.S. residents, please do not attempt to use the Platform or related
Services outside of the U.S.

"**Privacy Laws**" as used in this Privacy Policy means the California
Consumer Privacy Act of 2018, as amended by the California Privacy
Rights Act of 2020 (collectively, "**CCPA**"), the Tennessee Information
Protection Act, the Nebraska Data Privacy Act, the Maryland Online Data
Privacy Act of 2024, and any other U.S. state or federal laws governing
the collection, processing, and protection of Personal Information that
may be binding upon us and applicable to you.

"**Personal Information**" as used in this Privacy Policy means
information which identifies, relates to, describes, references, is
reasonably capable of being associated with, or could reasonably be
linked, directly or indirectly with a particular individual or
household, as defined under the Privacy Laws as "personal information,"
"personal data," "personally identifiable information," or the like;
however, it does not apply to: (a) publicly available information from
government records; and (b) de-identified and aggregated information.

## 1.2 Out-of-Scope

Please note that information collected and used outside of what's stated
in the above paragraphs is considered outside the scope of this Privacy
Policy. For example, this Privacy Policy **<u>does not</u>**
cover the following:

-   Information from individuals who have a business relationship with
    us, including veterinary users of our websites, platforms and
    services acting on behalf of their veterinary clinics. To learn more
    about how we collect and use information from individuals who have a
    business relationship with us, please see:
    https://www.vetcove.com/privacy/.

-   Information collected directly from your veterinary clinic or other
    third parties, even where the information is later used in
    connection with your use of the Platform or Services. The applicable
    veterinary clinic's or third-party's privacy policy govern the
    collection and use of that information.

Vetcove may process Personal Information for its own purposes and/or on
behalf of your veterinary clinic. Vetcove acts as a service provider or
processor when acting on behalf of your clinic, and your clinic handles
privacy requests related to your Personal Information under its control.
When acting for Vetcove's own purposes, such as maintaining platform
security or compliance with legal obligations, Vetcove acts as a
business or controller under applicable Privacy Laws. This Privacy
Policy covers both roles. To exercise rights related to
Vetcove-controlled data, please see *Section 6* (Your Rights and
Choices), below. If you wish to exercise your privacy rights regarding
Personal Information we process on behalf of your veterinary clinic,
please refer to your veterinary clinic's policies and contact your
veterinary clinic directly.

## 1.3 Minors

The Platform and Services are **<u>not</u>** intended for
children under eighteen (18) years of age. No one under the age of 18
may provide any information to, through or on the Platform. We do not
knowingly collect Personal Information from children under 18. If we
learn we have collected or received Personal Information from a child
under 18, we will delete that information. If you believe we might have
any information from or about a child under 18, please contact us using
any of the contact methods in the *<u>Contact Us</u>* section,
below.

# 2. The Information We Collect

The information we collect about you depends on how you use the Platform
and Services and the information you provide to us.

We collect information in three main ways: (1) information you provide
directly to us; (2) information we collect automatically through
technology when you use the Platform; and (3) information we collect
from other sources.

Some of the information identified below, including identifying
information, financial account information, and geolocation information,
may be considered sensitive under certain applicable Privacy Laws[^1].
If required under applicable law, we will collect and process sensitive
personal data only with your consent and only as reasonably necessary to
provide the requested services, to perform permitted business functions,
or as otherwise allowed or required by law. You may exercise your right
to limit or opt out of certain uses of sensitive personal information as
described in *Section 6* (Your Rights and Choices), below. If you choose
not to provide or allow us to collect some information, you may not be
able to take full advantage of the Platform and Services, and we may not
be able to fulfill your requests.

## 2.1 Information You Provide Directly to Us

When you use the Platform and Services, you may provide certain
information to us. For example, we collect information directly from you
when you create an account on the Platform, when you place an order or
purchase medications from your veterinarian through the Platform, when
you set up autoship for certain products or manage your subscriptions
for your pets through the Platform, or otherwise communicate and
interact with us in connection with your use of the Platform (*e.g*.,
chatting with our customer support team).

Some of the most common categories of Personal Information we collect
directly from you include:

-   **Contact and account information**, such as your name, shipping
    address, telephone number, email address, and login credentials.

-   **Profile information**, including information about your pet and
    other members of your family who may care for your pet.

-   **Financial and transactional information**, such as credit card and
    billing information.

-   **Customer service and clinic interaction information**, such as
    messages submitted to us or to your veterinary clinic through online
    forms, SMS or text messages, or emails associated with the Services,
    and summaries of voice recordings of interactions with our customer
    service personnel.

Additionally, in certain cases, we may collect demographic information
from you. For example, if you place an order for a controlled substance
or a state-regulated drug of concern through the Platform, we may
collect your date of birth and driver's license number on behalf of the dispensing pharmacy, where required by law.

## 2.2 Information Collected Automatically

We also collect certain information about you automatically when you use
the Platform and Services. The information we automatically collect
includes information about your access to and use of the Platform, such
as your IP address, browser language, the state from which you accessed
the Platform based on the location of your device, software and hardware
attributes (including device IDs, operating system, and browser type),
referring and exit URLs, the links you click and files you download,
pages viewed and the order of those pages, the amount of time spent on
particular pages, the terms you use in searches on the Platform, the
date and time you accessed the Platform or opened our emails, and other
similar information.

We and our service providers also use technologies, including cookies,
to automatically collect certain types of usage and device information
when you use the Platform, Services or interact with our emails. The
Platform utilizes cookies to enhance your shopping experience by
retaining information about the items in your cart and collecting
general data on Platform traffic and interactions. This data is used to
improve the Platform's functionality and to ensure we are offering the
best experience and tools to our users. These automated technologies may
be considered targeted advertising, or profiling, under applicable
Privacy Laws, and you may opt out from the use of these automated
technologies. For information about how to disable cookies and other
similar automated technologies, please see *Section 6* (Your Rights and
Choices), below. Please note that some Service features may be
unavailable as a result.

We use third-party analytics services on the Platform and for the
Services to better understand your online activity. For example, we use
Google Analytics and you can review the "How Google uses information
from sites or apps that use our services" linked here:
<https://policies.google.com/technologies/partner-sites>, for
information about how Google processes the information it collects.
These service providers collect information about your use of the
Platform and Services and other websites and online services over time
through cookies, device identifiers, or other tracking technologies. The
information collected may include your IP address, web browser, mobile
network information, pages viewed, time spent, links clicked, and
conversion information. We and our third-party partners use this
information to, among other things, analyze and track data and determine
the popularity of particular content, as well as to provide services to
us, such as reporting, analytics, and market research.

## 2.3 Information We Collect from Other Sources

We may receive Personal Information about you from other sources in
order to provide the Services to you on behalf of your veterinary
clinic. For example, we may receive information from your veterinarian
and veterinary clinic. This information may include your name, contact
data, information related to your pet's health records and prescription
histories.

## 2.4 De-Identified Data

We may de-identify information we collect so the information cannot
reasonably identify you or your device, or we may collect information
that is already in de-identified form. Our use and disclosure of
de-identified information is not subject to any restrictions under this
Privacy Policy, and we may use and disclose it to others for any
purpose, without limitation.

*<u>Table A</u>* below summarizes the categories of Personal
Information we may collect, including those we collected within the last
twelve (12) months:

<u>*Table A*</u>: Categories of Personal Information Collected

| Category | Examples | Collected |
| -------- | -------- | --------- |
| A. Identifiers | A real name, alias, postal address, date of birth, unique personal identifier, online identifier including Platform username, Internet Protocol address, telephone number, email address, or other similar identifiers. | YES |
| B. Personal information categories | A name, address, telephone number, insurance policy number, credit card, debit card number, or other financial information. (Some personal information included in this category may overlap with other categories.) | YES |
| C. Protected classification characteristics | Age (40 years or older). | YES |
| D. Commercial information | Pet name and information, including pet insurance information, pet medical records, and pet caretaker information if other than you; products or services purchased, obtained, or considered; other purchasing or consuming histories or tendencies; interests and preferences; Platform account log-in credentials, including passwords. | YES |
| E. Biometric information | Genetic, physiological, behavioral, and biological characteristics, or activity patterns used to extract a template or other identifier or identifying information, such as, fingerprints, faceprints, and voiceprints, iris or retina scans, keystroke, gait, or other physical patterns, and sleep, health, or exercise data. | NO |
| F. Internet or other similar network activity | Browsing history, search history, information on a consumer's interaction with the Platforms and Services (including our website), application, or advertisement. | YES |
| G. Geolocation data | Physical location, including location data during use of our Platforms and website. | YES |
| H. Sensory data | Audio, electronic, visual, or similar information, such as voice recordings when speaking with our customer service representatives. | YES |
| I. Professional or employment-related information | Current or past job history or performance evaluations. | NO |
| J. Non-public education information | Education records directly related to a student maintained by an educational institution or party acting on its behalf, such as grades, transcripts, class lists, student schedules, student identification codes, student financial information, or student disciplinary records. | NO |
| K. Inferences drawn from other personal information | Profile reflecting a person's preferences, characteristics, psychological trends, predispositions, behavior, attitudes, intelligence, abilities, and aptitudes. | YES |
| L. Sensitive personal information | Government identifiers, such as Social Security number, account access credentials, precise geolocation, racial or ethnic origin, citizenship or immigration status, religious or philosophical beliefs, union membership, communications not directed to us, genetic data, neural data, unique identifying biometric information, health information, or sexual orientation. | YES |

# 3. How We Use Your Information

We may use Personal Information we collect in the following ways:

-   **To Provide the Platform and Services**, such as to complete a
    transaction, ship a product purchased through the Platform and
    Services, maintain your account, offer customer support, and
    investigate and respond to your inquiries.

-   **To Communicate with You**, including keeping you informed about
    purchases, shipments, and changes to applicable terms, conditions,
    or policies. This may include enabling communications between you
    and your veterinarian or veterinary clinic, such as text message
    notifications.

-   **For Marketing and Promotions**, such as providing you with
    relevant announcements or offers related to the Platform and
    Services, and recommending veterinary products based on your
    activity. Subject to your consent where required, we may send these
    communications via the Platform, as well as through email, mail, or
    SMS. If you do not want us to use your information in this way,
    please contact us using any of the contact methods in the *Contact
    Us* section or update your preferences in your account settings.

-   **To Improve our Services and Platforms**, including bug detection
    and debugging, usage analysis, user experience improvements,
    research and development of new offerings, and customer support
    training. We may also use your Personal Information, including in
    deidentified or aggregated form, to develop, train, test, or improve
    artificial intelligence or machine learning systems, subject to
    appropriate safeguards and as permitted by law. Where required by
    applicable Privacy Laws, you may have rights related to profiling or
    automated decision-making.

-   **For Security and Other General Business Operations**, such as to
    maintain the integrity and security of the Platform and Services; to
    detect and investigate security incidents, malicious or illegal
    activity; to maintain the Platform, including infrastructure and
    hosting; and to receive legal, accounting, consulting, and
    administrative services for our operations. We may use your Personal
    Information to create de-identified or aggregated datasets for
    business purposes, which we may then use or disclose for any purpose
    as permitted by law.

-   **To Facilitate Mergers and Acquisitions**, such as to evaluate or
    conduct a merger, divestiture, restructuring, reorganization,
    dissolution, or other sale or transfer some or all of our assets,
    whether as a going concern or as part of bankruptcy, liquidation, or
    similar proceeding, in which Personal Information held by us about
    users of the Platform and Services is among the assets transferred.

-   **For Legal and Compliance**, Such as to comply with applicable laws
    and regulations, including Privacy Laws; responding to lawful
    requests or legal process; working with our vendor partners on any
    recalls or adverse events; meeting our pharmacovigilance
    obligations; enforcing our terms; or protecting our rights, users,
    partners, and the public.

-   **With Additional Notice or Consent**. We may also use your Personal
    Information for a specific purpose that we communicate to you. We
    will ask for your consent to process your Personal Information for
    such purpose where required in accordance with applicable legal
    requirements. We will not use your Personal Information for
    materially different or incompatible purposes without appropriate
    notice.

For the purposes discussed in this Privacy Policy, we may combine the
Personal Information that we collect through the Services with
information that we receive from other sources, both online and offline,
and use such combined information in accordance with this Privacy
Policy.

We do not engage in profiling in furtherance of decisions that produce
legal or similarly significant effects concerning individuals. As a
result, there is no profiling activity for you to opt out of under
applicable state Privacy Laws.

# 4. How We Disclose Your Information

We may disclose your Personal Information in the following ways:

-   **Authorized Third-Party Vendors and Service Providers**. We select
    and use third-party vendors to help us provide our Services and to
    operate our business. They provide a variety of services to us,
    including order fulfillment, shipping and delivery, customer
    service, web hosting, payment processing, sales and marketing, data
    storage, security, fraud prevention, research and legal, advising or
    consulting services. We share information with our fulfillment
    partners for the purpose of ensuring the delivery of the purchased
    product or requested services. This includes, but is not limited to,
    providing your information to the fulfilling pharmacy or to vendors
    that ship your order directly to you. Any information shared with
    Vetcove may be shared with Vetcove's Affiliates.

-   **Veterinary Clinic**. In partnering with your veterinary clinic to
    provide the Platform and Services, we collect and process personal
    information primarily on behalf of your veterinary clinic.
    Accordingly, information made available to us may be shared with
    your veterinary clinic, to enable the delivery of the Services and
    to assist your clinic in maintaining accurate records. This ensures
    that your veterinarian has the most comprehensive and current
    information to provide optimal care for your pet.

-   **Vendors and Suppliers**. We may disclose the information we
    collect about you to manufacturers, compounders, and other suppliers
    that have a business relationship with us, and that manufacture,
    compound or supply products purchased by you. For example, we may
    make available certain vendor or supplier promotions or loyalty
    programs that you may elect to participate in, and to document your
    participation, we will share your Personal Information with the
    particular manufacturer, compounder, or other supplier, in
    accordance with your selections and applicable law. Please note that
    vendor programs may be subject to additional terms and conditions.

-   **Legal Purposes**. We may disclose the Personal Information we
    collect about you if required to do so by law or applicable
    regulations, or in a good faith belief that such disclosure is
    reasonably necessary to: (a) comply with legal process (for example,
    a subpoena or court order); (b) enforce our Terms of Use, this
    Privacy Policy, or other contracts with you, including investigation
    of potential violations; (c) respond to claims that any content
    violates the rights of third parties; or (d) protect the rights,
    property, or personal safety of us or others.

-   **Regulatory Purposes**. At times, we may be required to share
    certain information with third parties in order to fulfill our
    regulatory obligations. This may include, but is not limited to,
    providing information to manufacturers for reporting adverse events,
    in order to enable them to perform the necessary reporting to
    regulatory agencies like the FDA, or to assist any of our
    manufacturer or vendor partners in the event that a product is
    recalled from the market.

-   **Business Transfers**. As we continue to develop our business, we
    may engage in transactions with other companies regarding the
    purchase or sale of a business. We may disclose your information to
    the extent necessary and under confidentiality obligations as part
    of, or in contemplation of, such transactions. If a portion or all
    of our assets are transferred to a third party in connection with
    such transactions, your information may be among the transferred
    assets.

We do not sell and have not sold Personal Information to any third
parties or our Affiliates, including for any marketing or promotional
purposes. We also do not share and have not, in the preceding twelve
(12) months, shared Personal Information with third parties for
cross-context behavioral advertising. If you choose to receive text
messages from us or your veterinary clinic, your veterinary clinic is
primarily responsible for obtaining your consent. However, Vetcove may
also collect and maintain opt-in records of consent for such messages.
Text messaging opt-in data and consent records are not shared with any
third parties, except as necessary to deliver messages or comply with
legal obligations.

*<u>Table B</u>* below details the categories of third parties to
whom we disclose Personal Information, including those we disclosed to
within the last twelve (12) months.

<u>*Table B*</u>: Categories of Third-Parties for Disclosures

| Personal Information Category | Business Purpose Disclosures | Sales |
| ----------------------------- | ---------------------------- | ----- |
| A: Identifiers | Vetcove's Affiliates, Service Providers, Product Manufacturers and Vendors, Your Veterinary Clinic, Internet cookie data recipients (e.g., Google Analytics), Government Agencies (if required) | None |
| B: Personal information categories | Vetcove's Affiliates, Service Providers, Product Manufacturers and Vendors, Your Veterinary Clinic, Internet cookie data recipients (e.g., Google Analytics), Government Agencies (if required) | None |
| C: Protected classification characteristics | Vetcove's Affiliates, Service Providers, Your Veterinary Clinic, Internet cookie data recipients (e.g., Google Analytics), Government Agencies (if required) | None |
| D: Commercial information | Vetcove's Affiliates, Service Providers, Product Manufacturers and Vendors, Your Veterinary Clinic, Internet cookie data recipients (e.g., Google Analytics), Government Agencies (if required) | None |
| E: Biometric information | None | None |
| F: Internet or other similar network activity | Vetcove's Affiliates, Service Providers, Your Veterinary Clinic, Internet cookie data recipients (e.g., Google Analytics), Government Agencies (if required) | None |
| G: Geolocation data | Vetcove's Affiliates, Service Providers, Your Veterinary Clinic, Internet cookie data recipients (e.g., Google Analytics), Government Agencies (if required) | None |
| H: Sensory data | Vetcove's Affiliates, Service Providers, Product Manufacturers and Vendors, Your Veterinary Clinic, Government Agencies (if required) | None |
| I: Professional or employment-related information | None | None |
| J: Non-public education information | None | None |
| K: Inferences drawn from other personal information | Vetcove's Affiliates, Service Providers, Internet cookie data recipients (e.g., Google Analytics), Government Agencies (if required) | None |
| L: Sensitive personal information | Vetcove's Affiliates, Service Providers, Your Veterinary Clinic, Government Agencies (if required) | None |

# 5. Security and Retention of Your Information

## 5.1 Security of Your Information

We are committed to protecting your information. We use a variety of
technical, physical, and organizational security measures designed to
protect against unauthorized access, alteration, disclosure, or
destruction of information. However, no security measures are perfect or
impenetrable. As such, we cannot guarantee the security of your
information.

When creating an account on the Platform, it is important that you
select a strong and unique password and do not disclose it to others.
You should also update your password from time to time. Alert us
immediately if you have any concerns about unauthorized use of your
account. Please also be cautious of phishing attempts or suspicious
emails. Vetcove will never ask you to provide sensitive account
information via email or text message. If you receive a suspicious
communication claiming to be from us, please contact us directly using
the information provided in the *Contact Us* section below.

## 5.2 Retention of Your Information

We retain information for different periods of time depending on the
purposes for which we collect and use it, as described in this Privacy
Policy. Where we process Personal Information on behalf of your
veterinary clinic, we retain such information in accordance with our
agreement with your clinic and their instructions. In limited
circumstances, where Vetcove processes Personal Information for its own
business purposes, we determine the retention periods based on the
criteria below. We will delete or de-identify information when it is no
longer needed to fulfill these purposes unless a longer retention period
is required to comply with applicable laws.

The criteria we use to determine our retention periods include the
following:

-   The length of time we have an ongoing relationship with you and
    provide the Platform and Services to you (for example, for as long
    as you have an account with us or continue using the Services).

-   Whether there is a legal obligation to which we are subject (for
    example, certain laws require record retention for a certain period
    of time).

-   Whether retention is advisable considering our legal position (such
    as, for statutes of limitations, litigation, or regulatory
    investigations).

# 6. Your Rights and Choices

We provide you with the ability to exercise certain controls and choices
regarding our collection, use and disclosure of your information. **We
will not discriminate against you for exercising any of the rights that
are provided to you under applicable Privacy Laws.** Unless otherwise
permitted by applicable law, we will not, as a result of the exercise of
your rights: (a) deny you products or services; (b) charge you different
prices or rates for products or services, including through granting
discounts or other benefits, or imposing penalties; (c) provide you a
different level or quality of products or services; or (d) suggest that
you may receive a different price or rate for products or services or a
different level or quality of products or services.

However, from time to time, we may offer you certain financial
incentives permitted by applicable law that can result in different
prices, rates, or quality levels. Any permitted financial incentive we
offer will reasonably relate to your Personal Information's value and
contain written terms that describe the program's material aspects.
Participation in a financial incentive program requires your prior
opt-in consent, which you may revoke at any time.

You may adjust the following controls via the Platform:

-   **Account profile.** You can update certain account profile
    information by logging into your account. For example, you may
    choose to update your username or password, or any addresses that
    you may have stored on file. To close your account, please contact
    us as detailed in the "Contact Us" section below.

-   **Communication preferences.** You can stop receiving marketing
    emails by selecting "Opt-out of marketing emails" in the
    communication preferences section of your account profile. In
    addition, you may control the messages you receive by selecting
    "Opt-out of SMS Updates" in your account settings. If you opt-out of
    SMS updates, you may still receive transactional messages sent
    directly by your veterinary clinic for important prescription or
    care-related communications. Please note that you cannot opt-out of
    essential service-related communications, such as account
    verification, order confirmations, or important technical or
    security notices.

-   **Cookie controls**. Many web browsers are set to accept cookies and
    similar tracking technologies by default. If you prefer, you can set
    your browser to delete or reject these technologies. If you choose
    to delete or reject these technologies, or opt-out of the use of
    cookies otherwise as set forth below, this could affect certain
    features of our Services. If you use a different device, change
    browsers, or delete the opt-out cookies that contain your
    preferences, you may need to perform the opt-out task again.

In addition to those controls set forth above, we provide the following
privacy rights to all individuals located in the United States.

**Important Note About Your Privacy Rights**: In most cases, Vetcove
processes your Personal Information on behalf of your veterinary clinic,
which acts as the data controller under applicable Privacy Laws. This
means that your veterinary clinic is primarily responsible for handling
and responding to privacy rights requests related to your Personal
Information processed on the clinic's behalf. If you wish to access,
correct, delete, or otherwise exercise rights over Personal Information
that we process on behalf of your veterinary clinic, please contact your
clinic directly.

## 6.1 Right to Know and Data Portability

You have the right to request that we disclose certain information to
you about our collection and use of your Personal Information over the
past twelve months. Once we receive your request and confirm your
identity as provided below, except as provided for in this section, we
will disclose to you categories of Personal Information collected about
you, categories of sources from which the Personal Information is
collected, the purpose for collecting or selling that Personal
Information, categories of third parties with whom we may share that
Personal Information, and the specific pieces of Personal
Information collected about you, subject to any permitted redactions.

## 6.2 Right to Delete

You have the right to request that we delete any of your Personal
Information that we have collected about you and retained, subject to
certain exceptions. Once we receive your request and confirm your
identity as set forth below, we will review your request to see if an
exception under applicable Privacy Laws allowing us to retain the
information applies. For information on retention, please see *Section
5* (Security and Retention of Your Information), above.

## 6.3 Right to Correct

You may request that we correct any inaccuracies in your Personal
Information that we maintain about you. Upon receipt of a verifiable
request, we will correct the inaccurate information as required by
applicable Privacy Laws unless we determine the Personal Information is
accurate, complete, and verifiable, or if the request is otherwise not
required or permitted under applicable law.

## 6.4 Opt-Out Rights

### 6.4.1 Opt-Out of Sharing of Personal Information/Targeted Advertising

You have the right to direct us to not sell or share your Personal
Information at any time. You also have the right to opt-out of the use
of your Personal Information for targeted advertising. However, we
currently do not sell or share Personal Information, including for
targeted advertising. Note that you also can use the Global Privacy
Control ("GPC") to signal your opt-out preference. To learn more about
the GPC, please visit https://globalprivacycontrol.org/.

### 6.4.2 Opt-Out of Cookies

There are several ways to opt-out of our use of cookies. You can set
your browser to refuse all or some browser cookies, or to alert you when
cookies are being sent. If, however, you do not accept any cookies, you
may experience some inconvenience in your use of the Platform and
Services.

<u>Promotional Offers from Vendors</u>. If you do not wish to
have your contact information used by Vetcove for promotional offerings,
you can opt-out by adjusting your user preferences in your account
profile or by contacting us using any of the methods listed in the
*Contact Us* section.

We do not control third parties' collection or use of your information
to serve interest-based advertising. However these third parties may
provide you with ways to choose not to have your information collected
or used in this way. You can opt out of receiving targeted ads from
members of the Network Advertising Initiative ("NAI") on the NAI's
website.

## 6.5 Limit Use of Sensitive Personal Data

You may request that we limit the use of any sensitive personal data. If
you limit the use, we will only use your sensitive personal data as
required by law.

## 6.6 Other Privacy Rights

Certain states[^2] permit resident users of our Services to request
certain information regarding our disclosure of Personal Information to
third parties for their direct marketing purposes. The only disclosure
of your Personal Information that we make to a third party for their
direct marketing purposes is to your veterinary clinics. To make a
request for information about this disclosure, please contact us using
any of the contact methods in the *<u>Contact Us</u>* section.

## 6.7 Exercising Your Rights and Our Response

### 6.7.1 Exercising Your Rights

To request access, correction, deletion, restriction, or confirmation of your Personal Information, or to exercise any other rights available to you under applicable Privacy Laws, contact us using any of the methods listed in the *<u>Contact Us</u>* section.

You do not need to create an account with us to submit a request to know
or delete. We will only use Personal Information provided in the request
to verify the requestor's identity or authority to make it.

### 6.7.2 Response Timing and Format

We will confirm receipt of your request. We endeavor to substantively
respond to a verifiable consumer request under this Privacy Policy
within forty-five (45) days of its receipt. If we require more time (up
to another 45 days), we will inform you of the reason and extension
period in writing. We may respond to your request through your account
with us, if you have one, or by mail or electronically.

We do not charge a fee to process or respond to your verifiable consumer
request unless it is excessive, repetitive, or manifestly unfounded. If
we determine that the request warrants a fee, we will tell you why we
made that decision and provide you with a cost estimate before
completing your request.

### 6.7.3 Right to Appeal

You may appeal our decision with respect to a request you have submitted
regarding your rights in relation to your Personal Information by
contacting us using any of the methods listed under *Contact Us*. We
will respond to your appeal within forty-five (45) days of its receipt.

# 7. Changes to Our Privacy Policy

We reserve the right to amend this Privacy Policy at our discretion and
at any time. When we make changes to this Privacy Policy, we will post
the updated version on the Platform and update the Privacy Policy's
modified on date on the top of the Privacy Policy. **Your continued use
of the Platform and Services following the posting of changes
constitutes your acceptance of such changes**.

# 8. Contact Us

If you have any questions or comments about this Privacy Policy, the
ways in which Vetcove collects and uses your information described here,
your choices and rights regarding such use, or wish to exercise any of
your rights in relation to your Personal Information, whether outlined
herein or otherwise as applicable under Privacy Laws, please do not
hesitate to contact us using any of the methods below:

<a href="${privacyRightsUrl}" target="_blank" rel="noopener noreferrer"><strong>Privacy Rights Request Form</strong></a>

**Email**: &#112;&#114;&#105;&#118;&#097;&#099;&#121;&#064;&#118;&#101;&#116;&#099;&#111;&#118;&#101;&#046;&#099;&#111;&#109;

**Postal Address**:<br/>
Vetcove Inc.<br/>
Attn: Privacy<br/>
382 NE 191st Street<br/>
PMB 46115<br/>
Miami, Florida 33179-3899

If you need to access this Privacy Policy in an alternative format due
to having a disability, please contact us using any of the methods
listed in this section.

[^1]: We do not use sensitive personal information (as defined under
    applicable U.S. state privacy laws, including the CCPA) for the
    purpose of inferring characteristics about a consumer. As such, and
    solely to the extent applicable under the Privacy Laws, sensitive
    personal information will be treated as Personal Information under
    this Privacy Policy.

[^2]: For example, as provided by California's "Shine the Light" law
    (Civil Code Section § 1798.83).

`;

const PrivacyPolicyPage = () => {
  const { isPhoneAndSmaller } = useBreakpoints();
  const isHdEnabled = useIsHdEnabled();
  const { clinic } = INITIAL_CLIENT_DATA;
  const privacyRightsUrl = clinic?.id
    ? `${marketingExternalUrls.typeformDataRequest}?utm_source=clinic_id_${clinic.id}`
    : marketingExternalUrls.typeformDataRequest;
  const markdown = getPrivacyPolicy(privacyRightsUrl);

  return (
    <Page
      Header={!isHdEnabled ? <StreamlinedHeader hasPrevious={true} /> : undefined}
      Footer={!isHdEnabled ? null : undefined}
    >
      {isHdEnabled && <div className={styles.gradient} />}
      {isHdEnabled && <div className={styles.background} />}
      <Subheader
        title="Privacy Policy"
        description={`Last Modified On: ${LAST_UPDATED_DATE}`}
        classNames={{ container: styles.subheader }}
        shouldCenterText={!isPhoneAndSmaller}
      />
      <Body className={styles.main}>
        <Markdown markdown={markdown} className={styles.content} hasHtml={true} />
      </Body>
    </Page>
  );
};

export default PrivacyPolicyPage;
